import React, { useCallback, useMemo } from 'react';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import './SamsManagementAreasFilter.css';

const AREA_NAMES: Record<string, string> = {
  all: 'All Areas (aggregate)',
  CA1: 'Closed Area I (CA1)',
  'CA2-N': 'Closed Area II North (CA2-N)',
  'CA2-S': 'Closed Area II South (CA2-S)',
  CAII: 'Closed Area II (CAII)',
  DMV: 'Delmarva (DMV)',
  ET: 'Elephant Trunk (ET)',
  HCS: 'Hudson Canyon South (HCS)',
  NLS: 'Nantucket Lightship (NLS)',
  NYB: 'New York Bight (NYB)',
  Open: 'Open Areas',
};

interface SamsManagementAreasFilterData {
  selectedSamsAreas?: string[];
  availableSamsAreas?: string[];
  onAreasChange?: (areas: string[]) => void;
  loading?: boolean;
  showAreaFilter?: boolean;
}

function getAreaDisplayName(areaCode: string): string {
  return AREA_NAMES[areaCode] ?? areaCode;
}

export default function SamsManagementAreasFilter(
  props: RegistryComponentProps
): React.ReactElement | null {
  const formContext = useFormContextOptional();
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<SamsManagementAreasFilterData>(props);

  const selectedYear = (formContext?.values?.['time-period'] as string) ?? 'projections';
  const showAreaFilter =
    data.showAreaFilter ??
    (pageData?.data?.selectedModel === 'CASA/SAMS') ??
    false;
  const selectedSamsAreas = useMemo(
    () =>
      data.selectedSamsAreas ??
      (pageData?.data?.selectedSamsAreas as string[] | undefined) ??
      ['all'],
    [data.selectedSamsAreas, pageData?.data?.selectedSamsAreas]
  );
  const availableSamsAreas = useMemo(
    () =>
      data.availableSamsAreas ??
      (pageData?.data?.availableSamsAreas as string[] | undefined) ??
      [],
    [data.availableSamsAreas, pageData?.data?.availableSamsAreas]
  );
  const onAreasChange =
    data.onAreasChange ?? (pageData?.actions?.onAreasChange as ((areas: string[]) => void) | undefined);
  const loading = data.loading ?? pageData?.loading ?? false;

  const handleAreaToggle = useCallback(
    (areaCode: string) => {
      if (!onAreasChange) return;

      if (areaCode === 'all') {
        if (selectedSamsAreas.includes('all')) {
          onAreasChange([]);
        } else {
          onAreasChange(['all']);
        }
      } else {
        if (selectedSamsAreas.includes('all')) {
          onAreasChange([areaCode]);
        } else {
          if (selectedSamsAreas.includes(areaCode)) {
            const newSelection = selectedSamsAreas.filter((a) => a !== areaCode);
            onAreasChange(newSelection.length === 0 ? ['all'] : newSelection);
          } else {
            const newSelection = [...selectedSamsAreas, areaCode];
            onAreasChange(newSelection);
          }
        }
      }
    },
    [onAreasChange, selectedSamsAreas]
  );

  if (!showAreaFilter || selectedYear !== 'projections') {
    return null;
  }

  return (
    <div className="sams-management-areas-filter area-filter-container">
      <label>SAMS Management Areas:</label>
      <div className="area-filter-description">
        Select areas to include in projections (CASA assessment always shows fleet-wide total)
      </div>
      <div className="area-checkboxes">
        <div className="area-checkbox-item">
          <label className="area-checkbox-label">
            <input
              type="checkbox"
              checked={selectedSamsAreas.includes('all')}
              onChange={() => handleAreaToggle('all')}
              disabled={loading}
            />
            <span className="area-checkbox-text">{getAreaDisplayName('all')}</span>
          </label>
        </div>

        {availableSamsAreas.length > 0 ? (
          availableSamsAreas.map((areaCode) => (
            <div key={areaCode} className="area-checkbox-item">
              <label className="area-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedSamsAreas.includes(areaCode)}
                  onChange={() => handleAreaToggle(areaCode)}
                  disabled={loading || selectedSamsAreas.includes('all')}
                />
                <span
                  className="area-checkbox-text"
                  style={{
                    opacity: selectedSamsAreas.includes('all') ? 0.5 : 1,
                  }}
                >
                  {getAreaDisplayName(areaCode)}
                </span>
              </label>
            </div>
          ))
        ) : (
          <div className="area-loading-message">Loading available areas...</div>
        )}
      </div>

      {selectedSamsAreas.length > 0 && !selectedSamsAreas.includes('all') && (
        <div className="area-selection-summary">
          Showing {selectedSamsAreas.length} area
          {selectedSamsAreas.length !== 1 ? 's' : ''}:{' '}
          {selectedSamsAreas.map(getAreaDisplayName).join(', ')}
        </div>
      )}
    </div>
  );
}
